/**
 * SignupStepper.tsx
 *
 * Visual 3-step progress stepper for the signup flow.
 * Extracted from example/mvp/signup.html (.portal-stepper).
 *
 * Steps (fixed for this project):
 *   1. Your details
 *   2. Verify phone
 *   3. Create password
 *
 * ACUL NOTE
 * ---------
 * Steps 1 and 3 map directly to Auth0 ACUL screens:
 *   Step 1 → signup-id
 *   Step 3 → signup-password
 *
 * Step 2 (Verify phone) is aspirational in this UI — it maps to the
 * mfa-sms-enrollment / mfa-sms-challenge ACUL screens triggered when your
 * tenant requires phone MFA on first login, or a Post-Registration Action.
 * TODO: configure Auth0 MFA policy or Post-Registration Action for phone OTP.
 *
 * Junior devs: this is purely visual — it does not drive navigation.
 * The ACUL SDK drives actual screen transitions.
 */

interface SignupStepperProps {
  /** 1-based index of the currently active step */
  activeStep: 1 | 2 | 3;
}

const STEPS = [
  { id: 1, label: "Your details" },
  { id: 2, label: "Verify phone" },
  { id: 3, label: "Create password" },
] as const;

// Checkmark SVG
function CheckIcon() {
  return (
    <svg
      className="portal-stepper__check"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3 8 L7 12 L13 4"
        stroke="currentColor"
        strokeWidth={2.4}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function SignupStepper({ activeStep }: SignupStepperProps) {
  return (
    <ol className="signup-stepper" aria-label="Sign up progress">
      {STEPS.map((step) => {
        const isDone = step.id < activeStep;
        const isActive = step.id === activeStep;

        const itemClass = [
          "signup-stepper__item",
          isActive ? "signup-stepper__item--active" : "",
          isDone ? "signup-stepper__item--done" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <li key={step.id} className={itemClass} data-stepper={step.id}>
            <button
              type="button"
              className="signup-stepper__btn"
              disabled={!isDone && !isActive}
              aria-current={isActive ? "step" : undefined}
            >
              <span className="signup-stepper__num" aria-hidden="true">
                {isDone ? (
                  <CheckIcon />
                ) : (
                  <span className="signup-stepper__num-text">{step.id}</span>
                )}
              </span>
              <span className="signup-stepper__label">{step.label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}
